/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals console */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Textbox from '../Textbox/Textbox';
import Radio from '../Radio/Radio';
import Checkbox from '../Checkbox/Checkbox';
import Label from '../Label/Label';
import Button from '../Button/Button';
import Service from '../Util/Service';
import $ from '../Util/Util';
import './Groupview.css';

/**
 * Represents the group view.
 */

export default class Groupview extends Component {

    /**
     * Properties.
     */

    static propTypes = {
        context:            PropTypes.object.isRequired,
        onLoading:          PropTypes.func.isRequired,
        onClose:            PropTypes.func.isRequired,
        onDelete:           PropTypes.func
    }

    /**
     * Default state.
     */

    state = {
        edit:               false,
        group:              null,
        users:              []
    }

    /**
     * Event that fires when the user wants to modify the selected group.
     */

    _modify = () => {

        // Setting the maintenance state

        this.setState({ edit: true }, () => {

            // Refreshing the screen

            this._refresh();
        });
    }

    /**
     * Event that fires when the user wants to delete the selected group.
     */

    _delete = () => {
        this.props.onDelete(this.state.group);
    }

    /**
     * Event that fires when the user cancels the current maintenance.
     */

    _cancel = () => {
        this.props.onClose();
    }

    /**
     * Saves the group.
     */

    _save = () => {

        // Validating name

        if (this.name.isEmpty()) {

            // Showing error

            $.fireEvent('snackbar', { message: this.props.context.locale.group.validation.missingname, error: true });

            return;
        }

        // Showing progress indicator

        this.props.onLoading(true);

        // Creating the group

        let group = {
            name:           this.name.getValue(),
            defaultaccess:  this.defaultaccess0.getValue() ? 'r' : (this.defaultaccess1.getValue() ? 'rw' : null),
            members:        []
        };

        // Setting the group ID in case of modification

        if (this.state.group.id) {
            group.id = this.state.group.id;
        }

        // Adding the members to the group

        this.member.forEach((item) => {
            if (item.checkbox && item.checkbox.getValue()) {
                group.members.push(item.uid);
            }
        });

        // Saving the group

        Service.saveGroup(group).then(() => {

            // Hiding progress indicator

            this.props.onLoading(false);

            // Showing error

            $.fireEvent('snackbar', { message: this.props.context.locale.group.savedmessage });

            // Closing the view

            this.props.onClose(true);
        }).catch((error) => {

            // Hiding progress indicator

            this.props.onLoading(false);

            // Showing error

            $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
        });
    }

    /**
     * Refreshes the screen.
     */

    _refresh = () => {

        // Setting the group name

        this.name.setValue(this.state.group.name);

        // Setting the default group access

        if (this.state.group.defaultaccess === 'r') {
            this.defaultaccess0.setValue(true);
        } else if (this.state.group.defaultaccess === 'rw') {
            this.defaultaccess1.setValue(true);
        } else {
            this.defaultaccess2.setValue(true);
        }
    }

    /**
     * Toggles the specified member chechkbox.
     * 
     * @param   {String}    uid     UID of the member user
     */

    _toggleMember = (uid) => {

        // Searching the member by UID

        for (let i = 0; i < this.member.length; i++) {
            if (this.member[i].uid === uid && this.member[i].checkbox) {

                // Toggling the checkbox

                this.member[i].checkbox.toggle();
                break;
            }
        }
    }

    /**
     * Loads the specified group.
     * 
     * @param   {Object}    group   group that has to load
     */

    load = (group) => {

        // Showing the progress indicator

        this.props.onLoading(true);

        // Loading the groups

        Service.loadGroup().then((groups) => {

            // Loading the users

            Service.loadUser().then((users) => {

                // Setting the assigned members

                if (group.members) {
                    groups.forEach(item => {
                        if (item.members) {
                            for (let i = 0; i < item.members.length; i++) {
                                for (let j = 0; j < users.length; j++) {
                                    if (users[j].uid === item.members[i].uid) {
                                        users.splice(j, 1);
                                        break;
                                    }
                                }
                            }
                        }
                    });
                }

                this.setState({ users: users, group: group, edit: group.id === undefined }, () => {

                    // Hiding the progress indicator

                    this.props.onLoading(false);

                    if (!group.id) {
                        this._refresh();
                    }  
                });
            }).catch((error) => {

                // Hiding the progress indicator

                this.props.onLoading(false);

                // Showing error

                $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
            });
        }).catch((error) => {

            // Hiding the progress indicator

            this.props.onLoading(false);

            // Showing error

            $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
        });
    }

    /** 
     * Group view renderer.
     */

    render() {

        // Preparing

        this.member = [];

        /**
         * Renders the entity creation access.
         * 
         * @param {Object}      group       group that has to render 
         * @param {Number}      index       index of the group
         * @param {Boolean}     checked     selected state of the creation access
         */

        let renderMember = (user, index, checked) => {
            return (
                <div key={index} className="groupview-member horizontal center">
                    <Checkbox ref={c => {this.member.push({ uid: user.uid, checkbox: c })}} checked={checked} tooltip={user.name + ' [' + user.email + ']'} />
                    <Label leftgap={8} text={user.name + ' [' + user.email + ']'} onTouch={this._toggleMember.bind(this, user.uid)} />
                </div>
            );
        };

        return (
            <div className="groupview" style={{ height: (this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.usefullheight - 32) : (this.props.context.environment.screen.usefullheight - 64)) + 'px' }}>

                {/* Group container */}

                <div className="groupview-scroll-container" style={{ height: (this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.usefullheight - 92) : (this.props.context.environment.screen.usefullheight - 120)) + 'px' }}>
                    
                    {/* Edit mode */}

                    { this.state.edit && this.state.group && <div className="vertical">

                        {/* Group name */}

                        <Textbox ref={c => {this.name = c;}} label={this.props.context.locale.group.field.name.label} tooltip={this.props.context.locale.group.field.name.tooltip} comment={this.props.context.locale.group.field.name.comment} placeholder={this.props.context.locale.group.field.name.placeholder} maxlength={40} labelposition="top" width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 64) : 320} isrequired />

                        {/* Default access */}

                        <Label text={this.props.context.locale.group.field.defaultaccess.label} bottomgap={4} />
                        <Radio ref={c => {this.defaultaccess0 = c;}} label={this.props.context.locale.group.field.defaultaccess.rights.r.label} tooltip={this.props.context.locale.group.field.defaultaccess.rights.r.tooltip} index={0} holder={this} group="defaultaccess" />
                        <Radio ref={c => {this.defaultaccess1 = c;}} label={this.props.context.locale.group.field.defaultaccess.rights.rw.label} tooltip={this.props.context.locale.group.field.defaultaccess.rights.r.tooltip} index={1} holder={this} group="defaultaccess" topgap={4} />
                        <Radio ref={c => {this.defaultaccess2 = c;}} label={this.props.context.locale.group.field.defaultaccess.rights.noaccess.label} tooltip={this.props.context.locale.group.field.defaultaccess.rights.noaccess.tooltip} index={2} holder={this} group="defaultaccess" topgap={4} />

                        {/* Members */}

                        <Label text={this.props.context.locale.group.field.members.label} topgap={16} bottomgap={4} />

                        {/* Assigned members */}

                        { this.state.group.members.map((item, index) => {
                            return renderMember(item, index, true);
                        })}

                        {/* Not assigned members */}

                        { this.state.users.map((item, index) => {
                            return renderMember(item, index, false);
                        })}
                    </div> }

                    {/* View mode */}

                    { !this.state.edit && this.state.group && <div className="vertical">

                        {/* Group name */}

                        <Label text={this.props.context.locale.group.field.name.label} />
                        <div className="groupview-value">{ this.state.group.name }</div>

                        {/* Default access */}

                        <Label text={this.props.context.locale.group.field.defaultaccess.label} topgap={16} />
                        <div className="groupview-value">{ this.state.group.defaultaccess === 'r' ? this.props.context.locale.group.field.defaultaccess.rights.r.label : (this.state.group.defaultaccess === 'rw' ? this.props.context.locale.group.field.defaultaccess.rights.rw.label : this.props.context.locale.group.field.defaultaccess.rights.noaccess.label) }</div>

                        {/* Members */}

                        <Label text={this.props.context.locale.group.field.members.label} topgap={16} />
                        { (!this.state.group.members || this.state.group.members.length === 0) && <div className="groupview-value">-</div> }
                        { this.state.group.members.map((item, index) =>
                            <div key={index} className="groupview-value">{ item.name } [<a className="userview-value-email" href={`mailto:${item.email}`}>{item.email}</a>]</div>
                        )}
                    </div> }
                </div>

                {/* Buttons */}

                <div className="groupview-buttons horizontal">

                    {/* Delete */}

                    { !this.state.edit && this.state.group && <Button label={this.props.context.locale.group.buttons.delete.label} tooltip={this.props.context.locale.group.buttons.delete.tooltip} width={60} onTouch={this._delete} /> }

                    {/* Cancel */}

                    { this.state.edit && <Button label={this.props.context.locale.group.buttons.cancel.label} tooltip={this.props.context.locale.group.buttons.cancel.tooltip} width={60} onTouch={this._cancel} /> }

                    {/* Spacer */}

                    <div className="flex"></div>

                    {/* Modify */}

                    { !this.state.edit && this.state.group && <Button label={this.props.context.locale.group.buttons.modify.label} tooltip={this.props.context.locale.group.buttons.modify.tooltip} width={60} onTouch={this._modify} /> }

                    {/* Save */}

                    { this.state.edit && <Button label={this.props.context.locale.group.buttons.save.label} tooltip={this.props.context.locale.group.buttons.save.tooltip} width={60} onTouch={this._save} /> }
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Groupview/Groupview.js